import { JSX, useContext, useEffect, useState } from "react";
import { MfaScanAuthenticatorQr } from "./steps/mfaScanAuthenticatorQr.js";
import { MfaEndSuccess } from "./steps/mfaEndSuccess";
import { MfaInitial } from "./steps/mfaInitial";
import { PspContext } from "../../config/contexts";
import { MfaSelectMethod } from "./steps/mfaSelectMethod.js";
import { MfaInputEmailCode } from "./steps/mfaInputEmailCode.js";
import { MfaMethod } from "@/api/openapi/types.gen.js";
import { getMfaMethods, getUserMe } from "@/api/openapi/sdk.gen.js";
import { toast } from "react-toastify";
import { translate } from "@/config/translator.js";

export type MfaMethodType = "email" | "authenticator";
export type MfaSetupContext = {
  chosenMethod?: MfaMethodType;
  email?: string;
  newMethod?: MfaMethod;
  emailMethod?: MfaMethod | null;
};
type MfaSetupStep =
  | "initial"
  | "chooseMethod"
  | "endSuccess"
  | "inputEmailCode"
  | "scanQr";

type MfaSetupSteps = Record<
  MfaSetupStep,
  {
    children: JSX.Element;
    getNext: (ctx?: MfaSetupContext) => MfaSetupStep | null;
    getPrev: () => MfaSetupStep | null;
  } | null
>;

export function useMfaSetup() {
  const { mfaSetupState, setMfaSetupState, lang, setUser } =
    useContext(PspContext);

  const [currentStep, setCurrentStep] = useState<MfaSetupStep>(
    mfaSetupState === "firstSetup" ? "initial" : "chooseMethod",
  );
  const [mfaSetupContext, setMfaSetupContext] = useState<MfaSetupContext>({});

  useEffect(() => {
    if (mfaSetupState === "closed") return;
    setCurrentStep(mfaSetupState === "firstSetup" ? "initial" : "chooseMethod");
    getMfaMethods({ query: { active_only: false, verified_only: false } }).then(
      ({ error, data }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          const emailMethod = data.find((method) => method.mfaType === "email");
          setMfaSetupContext((prev) => ({
            ...prev,
            emailMethod: emailMethod ?? null,
          }));
        }
      },
    );
  }, [lang, mfaSetupState]);

  const steps: MfaSetupSteps | undefined =
    mfaSetupState === "firstSetup"
      ? {
          initial: {
            children: <MfaInitial onNext={onNext} onBack={onBack} />,
            getNext: () => "scanQr",
            getPrev: () => null,
          },
          scanQr: {
            children: (
              <MfaScanAuthenticatorQr
                onNext={onNext}
                onBack={onBack}
                setMfaSetupContext={setMfaSetupContext}
              />
            ),
            getNext: () => "endSuccess",
            getPrev: () => "initial",
          },
          endSuccess: {
            children: (
              <MfaEndSuccess
                mfaSetupContext={mfaSetupContext}
                onNext={onNext}
              />
            ),
            getNext: () => null,
            getPrev: () => null,
          },
          chooseMethod: null,
          inputEmailCode: null,
        }
      : mfaSetupState === "createNew"
        ? {
            chooseMethod: {
              children: (
                <MfaSelectMethod
                  setMfaSetupContext={setMfaSetupContext}
                  mfaSetupContext={mfaSetupContext}
                  onNext={onNext}
                  onBack={onBack}
                />
              ),
              getNext: (ctx) =>
                ctx?.chosenMethod === "authenticator"
                  ? "scanQr"
                  : "inputEmailCode",
              getPrev: () => null,
            },
            scanQr: {
              children: (
                <MfaScanAuthenticatorQr
                  onNext={onNext}
                  onBack={onBack}
                  setMfaSetupContext={setMfaSetupContext}
                />
              ),
              getNext: () => "endSuccess",
              getPrev: () => "chooseMethod",
            },
            inputEmailCode: {
              children: (
                <MfaInputEmailCode
                  mfaSetupContext={mfaSetupContext}
                  setMfaSetupContext={setMfaSetupContext}
                  onNext={onNext}
                  onBack={onBack}
                />
              ),
              getNext: () => "endSuccess",
              getPrev: () => "chooseMethod",
            },
            endSuccess: {
              children: (
                <MfaEndSuccess
                  mfaSetupContext={mfaSetupContext}
                  onNext={onNext}
                />
              ),
              getNext: () => null,
              getPrev: () => null,
            },
            initial: null,
          }
        : undefined;

  function onNext() {
    const nextStep = steps?.[currentStep]?.getNext(mfaSetupContext) ?? null;
    if (nextStep) {
      setCurrentStep(nextStep);
    } else {
      setCurrentStep("initial");
      setMfaSetupState("closed");

      // refresh the user once mfa is set up
      getUserMe().then(({ data, error }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          setUser(data);
        }
      });
    }
  }

  function onBack() {
    const prevStep = steps?.[currentStep]?.getPrev() ?? null;
    if (prevStep) {
      setCurrentStep(prevStep);
    } else {
      setCurrentStep("initial");
      setMfaSetupState("closed");
    }
  }
  return { steps, currentStep, mfaSetupState, setMfaSetupState };
}
